import React, { InputHTMLAttributes, useRef, useState } from 'react';
import './Input.css';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

const Input: React.FC<InputProps> = ({ label, error, className = '', ...props }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [internalValue, setInternalValue] = useState<string>(String(props.defaultValue ?? ''));
  const inputType = props.type || 'text';
  const supportsClear = ![
    'file',
    'checkbox',
    'radio',
    'hidden',
    'submit',
    'button',
    'reset'
  ].includes(inputType);
  const currentValue = props.value !== undefined ? String(props.value) : internalValue;
  const showClearButton = supportsClear && !props.disabled && !props.readOnly && currentValue.length > 0;

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    if (props.value === undefined) {
      setInternalValue(event.target.value);
    }
    props.onChange?.(event);
  };

  const handleClear = () => {
    const inputElement = inputRef.current;
    if (!inputElement) return;

    const nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
    nativeSetter?.call(inputElement, '');
    const inputEvent = new Event('input', { bubbles: true });
    inputElement.dispatchEvent(inputEvent);
    if (props.value === undefined) {
      setInternalValue('');
    }
    inputElement.focus();
  };

  return (
    <div className="input-wrapper">
      {label && <label className="input-label">{label}</label>}
      <div className="input-field-wrap">
        <input
          ref={inputRef}
          className={`input ${showClearButton ? 'input-has-clear' : ''} ${error ? 'input-error' : ''} ${className}`}
          {...props}
          onChange={handleChange}
        />
        {showClearButton && (
          <button
            type="button"
            className="input-clear-button"
            onClick={handleClear}
            aria-label="Clear field"
            title="Clear"
          >
            x
          </button>
        )}
      </div>
      {error && <span className="input-error-message">{error}</span>}
    </div>
  );
};

export default Input;
